import { z } from 'zod';
import { mailFields, organizationColors } from './organization.js';
import { DEFAULT_IMPRINT_HTML, DEFAULT_PRIVACY_HTML } from './legal.js';
/** `legal`'s fields deliberately carry NO per-field default (unlike
 * `loginRateLimit`'s `maxAttempts`/`windowMinutes`, which are simple enough
 * that always resetting an omitted one to its default is fine): saving just
 * `{ legal: { privacyHtml: '...' } }` must leave `imprintHtml` exactly as it
 * was, not silently reset it back to `DEFAULT_IMPRINT_HTML`. A per-field
 * `.default()` here would defeat that — `ZodValidationPipe` runs on the
 * *request*, before `PlatformSettingsService.update`'s merge ever sees the
 * value, so a missing key would already have been filled with its default
 * by then. `platformSettings.legal` below still gets a full, concrete
 * default via `.default({...})` at the OBJECT level (`getRaw()` always
 * returns real strings), exactly like `loginRateLimit`. */
const legalContent = z.object({
    imprintHtml: z.string().max(200_000),
    privacyHtml: z.string().max(200_000),
});
/** Platform-wide settings — not scoped to any organisation.
 * `GET/PATCH /platform/settings`, platform admin only. */
export const platformSettings = z.object({
    /** How many `POST /auth/login` attempts are allowed within `windowMinutes`
     * before further attempts are rejected with `429 rate_limited` — counted
     * separately per client IP address and per attempted e-mail address
     * (whichever limit is hit first blocks that specific attempt). Protects
     * against both credential stuffing (many e-mails tried from one IP) and
     * password guessing (many passwords tried for one e-mail). Too low and
     * legitimate use gets blocked too (e.g. an admin switching between
     * several test accounts from the same connection); too high weakens the
     * brute-force protection. Applies immediately to every login attempt on
     * the platform — no restart or per-organisation opt-in needed. */
    loginRateLimit: z
        .object({
        maxAttempts: z.number().int().min(3).max(1000).default(10),
        windowMinutes: z.number().int().min(1).max(1440).default(15),
    })
        .default({ maxAttempts: 10, windowMinutes: 15 }),
    /** Fallback design values used for every organisation that hasn't set its
     * own (Verein › Design), field by field — see OrganizationResponse's
     * `platformDefaultColors`. An org's own value always wins when set;
     * otherwise this; otherwise the app's hard-coded built-in look. Changing
     * this here applies immediately to every organisation that hasn't
     * overridden that particular field.
     *
     * `logoFileId`/`backgroundImageFileId` follow the same fallback but are
     * `nullable` rather than merely `optional`: an explicit `null` means
     * "cleared, no platform default" and must survive JSON serialization to
     * actually reach the server (an `undefined` key is dropped by
     * `JSON.stringify` before the request body is even sent, which would
     * leave the previous value in place instead of resetting it — see
     * `PlatformDesignPage.tsx`'s reset buttons). */
    design: z
        .object({
        colors: organizationColors.optional(),
        /** Dark-Mode-Variante, siehe organizationProfile.colorsDark in organization.ts. */
        colorsDark: organizationColors.optional(),
        logoFileId: z.string().uuid().nullable().optional(),
        backgroundImageFileId: z.string().uuid().nullable().optional(),
    })
        .default({}),
    /** Platform-wide default SMTP server (Plattform › SMTP Server) — the
     * fallback every organisation uses unless it sets its own override
     * (`organizationSettings.mail`, `mode: 'custom'`), which in turn falls
     * back to this whenever no `host` is set here either. See
     * `resolveMailConfig` in `@bm/db` and docs/mail.md. */
    mail: mailFields.default({}),
    /** Impressum/Datenschutzerklärung (Plattform › Impressum & Datenschutz) —
     * frei formatierbares HTML aus dem Tiptap-Editor (`RichTextEditor.tsx`),
     * öffentlich ausgeliefert über `GET /public/legal` (siehe
     * `PublicLegalController`) und dort per DOMPurify sanitisiert gerendert.
     * Der Objekt-Default (nicht je Feld, siehe `legalContent` unten) ist der
     * vollständige Startinhalt, kein leerer String — ein frisch aufgesetztes
     * System zeigt sofort ein vollständiges Impressum (bis auf die eine
     * markierte offene Pflichtangabe), keine leere Seite. */
    legal: legalContent.default({ imprintHtml: DEFAULT_IMPRINT_HTML, privacyHtml: DEFAULT_PRIVACY_HTML }),
});
export const updatePlatformSettingsRequest = platformSettings.partial().extend({
    legal: legalContent.partial().optional(),
});
//# sourceMappingURL=platform-settings.js.map